import React, { useEffect, useState } from 'react'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import styled from 'particles/themes/styled'
import { ReportSummaryContainer } from '../../utils'
import Tab, { TabOption } from 'particles/Tab'
import { CampaignState } from 'state/campaigns'
import { TableViewType } from 'components/Shared/MuiTable'
import { getTopPerformersColumnsDef, getTopPerformersRowsDef, TopPerformersRowProps } from './TopPerformersTableDef'
import {
    getTopPerformers,
    selectTopPerformers,
    UpdateAmbassadorFavoritePayload,
    UpdateAmbassadorFavorite,
} from 'state/campaignReport'
import { useDispatch, useSelector } from 'react-redux'
import { TopPerformersTab } from 'state/campaignReport'
import { useNewTranslation } from 'hooks'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { ReportContainer } from '../../styles'
import { TFunction } from 'i18next'

interface NavBarProps {
    className?: string
    onSelectTab: (selectedTab: TopPerformersTab) => void
}

const tabOptions = (onSelectTab: (selectedTab: TopPerformersTab) => void, t: TFunction): TabOption[] => {
    return [
        {
            id: 'most_engagement',
            label: t('submission.campaignReport.topPerformer.tabs.mostEngagement'),
            onClick: () => onSelectTab('most_engagement'),
        },
        {
            id: 'highest_engagement_rate',
            label: t('submission.campaignReport.topPerformer.tabs.highestEngagementRate'),
            onClick: () => onSelectTab('highest_engagement_rate'),
        },
        {
            id: 'highest_content_engagement_rate',
            label: t('submission.campaignReport.topPerformer.tabs.highestContentEngagement'),
            onClick: () => onSelectTab('highest_content_engagement_rate'),
        },
        {
            id: 'most_views',
            label: t('submission.campaignReport.topPerformer.tabs.mostViews'),
            onClick: () => onSelectTab('most_views'),
        },
    ]
}

const NavBar: React.FC<NavBarProps> = ({ onSelectTab }) => {
    const [selectedTab, setSelectedTab] = useState<TopPerformersTab>('most_engagement')
    const [t] = useNewTranslation()
    const handlePerformanceTabClick = (performanceType: TopPerformersTab) => {
        onSelectTab(performanceType)
        setSelectedTab(performanceType)
    }

    return (
        <NavbarWrapper>
            <Tab
                tabItemAlign="left"
                tabs={tabOptions(handlePerformanceTabClick, t)}
                selectedTab={selectedTab}
                form="secondary"
                isDisabledClickable={false}
                className="metrics-tab"
            />
        </NavbarWrapper>
    )
}

interface TopPerformersProps {
    campaign: CampaignState
}

const TopPerformers: React.FC<TopPerformersProps> = ({ campaign }) => {
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const topPerformers = useSelector(selectTopPerformers)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetTopPerformers]))
    const [selectedTab, setSelectedTab] = useState<TopPerformersTab>('most_engagement')

    const handleClickFavorite = (payload: UpdateAmbassadorFavoritePayload) => {
        const { favorited, ...rest } = payload

        dispatch(
            UpdateAmbassadorFavorite({
                ...rest,
                favorited: !favorited,
            }),
        )
    }

    const topPerformersColumns = getTopPerformersColumnsDef(campaign, selectedTab, handleClickFavorite)
    const topPerformersRows = getTopPerformersRowsDef(topPerformers, selectedTab)

    const handleSelectTab = (tab: TopPerformersTab) => {
        setSelectedTab(tab)
    }

    useEffect(() => {
        dispatch(getTopPerformers({ uuid: campaign.uuid, tab: selectedTab, top: 5 }))
    }, [selectedTab])

    if (!campaign) return null

    return (
        <>
            <NavBar onSelectTab={handleSelectTab} />
            <ReportContainer>
                {topPerformersRows.map((task, index) => {
                    return (
                        <ReportSummaryContainer
                            key={index}
                            title={task.title}
                            description={t('submission.campaignReport.topPerformer.ranking.subtext', {
                                rankingNumber: 5,
                                totalPartiposters: task.total_users,
                            })}
                        >
                            <TableContainer>
                                <PartiTable
                                    tableHeight="438px"
                                    viewType={TableViewType.TOP_PERFORMERS}
                                    headerHeight={56}
                                    rowHeight={76}
                                    getRowHeight={() => 'auto'}
                                    columns={topPerformersColumns}
                                    rows={task.ranking as TopPerformersRowProps[]}
                                    isLoading={isLoading}
                                    hideToolbar
                                    disableColumnMenu
                                    hideFooter
                                    noPadding
                                    hideTableBorder
                                    isNewStyle
                                />
                            </TableContainer>
                        </ReportSummaryContainer>
                    )
                })}
            </ReportContainer>
        </>
    )
}

export default TopPerformers

const TableContainer = styled.div`
    width: 100%;
    .MuiDataGrid-columnHeaders {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => theme.colors.surface.infoPastel};
    }

    .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox) .MuiDataGrid-columnHeaderTitleContainer {
        margin-right: 0;
    }

    .new-style-MUI .MuiDataGrid-columnHeaderTitle {
        color: ${({ theme }) => theme.colors.text.primary};
    }

    .align-left-header {
        .MuiDataGrid-columnHeaderTitleContainerContent {
            margin-left: 2px;
        }
    }

    .MuiDataGrid-columnHeaderTitleContainerContent {
        margin: auto;
    }

    .title-cell {
        background: ${({ theme }) => theme.colors.surface.infoPastel};
    }

    .row-total {
        min-height: 36px;
        height: 36px;
        align-items: center;
        background: ${({ theme }) => theme.colors.surface.tertiary};

        .title-cell {
            min-height: 36px;
            height: 36px;
            background: ${({ theme }) => theme.colors.surface.tertiary};
        }
    }
`

export const NavbarWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding-bottom: 16px;
    background: ${({ theme }) => theme.colors.background.primary};

    .metrics-tab {
        background: ${({ theme }) => theme.colors.background.primary};
    }
`
